import React, { useEffect, useMemo, useState } from "react";
import { Formik, Form, Field, FieldProps } from "formik";
import * as Yup from "yup";
import { Box, Button, Checkbox, FormControlLabel, Stack,Grid,Divider,Theme,CircularProgress, Typography} from "@mui/material";
import { getFieldComponent } from "./form_builder/fields";
import RequiredFields from "./RequiredFields";
import { createStyles, makeStyles } from "@mui/styles";
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useStore } from 'store/StoreProvider';
import { useSession } from 'hooks/SessionProvider';
import { useFormConfig } from "./formConfig/ValidationsSchema";
import { getInitialValues } from "./formConfig/initialValues";
import { postPatientData,Params } from "services/apiService";
import { customRoutes } from "utils/constants";
import useUpdatePatientForm from "hooks/useUpdatePatientForm";
import Errorview from "./ErrorView";
import { ErrorMessages , termsAcceptedLabel } from "utils/constants";
import { BallTriangle } from '@agney/react-loading';
import { useRoute } from "hooks/RouteProvider";

const useStyles = makeStyles((theme:Theme) =>
  createStyles({
      dialogFooterBody:{
          display:"flex",
          justifyContent:"space-between",
          alignItems:"center",
         
      },
      backButton:{
          borderRadius:"10px !important",
          borderColor:"#d6d6d6 !important",
          color:"#004852 !important",
          backgroundColor: 'white !important',
          '&:hover': {
          backgroundColor: '#f6f6f6 !important',
        },
          [theme.breakpoints.up('md')]:{
              paddingLeft:"40px !important",
              paddingRight:"40px !important",
          }
      }, 
      
      continueButton:{
          backgroundColor:"#3D5A80 !important",
          borderRadius:"10px !important",
          borderColor:"#d6d6d6 !important",
          color:"#fff !important",
          '&:hover': {
          backgroundColor: '#fff !important',
          color:"#3D5A80 !important"
        },
          [theme.breakpoints.up('md')]:{
              width:"200px !important"
          },
          width:"150px !important"
      },
      icon: {
          position: 'absolute',
          right: '10px',
        },
        text: {
          flexGrow: 1,
          textAlign: 'center',
        },
        formkMargins:{
          marginTop:"5px"
        },
        loaderMainBody:{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          height: "45vh",
        },
        loader:{
          display: "flex",
          alignItems: "center",
          textAlign: "center",
        }
  })
);

interface FormBuilderProps {
  formFields: any;
}

const FormBuilder: React.FC<FormBuilderProps> = ({ formFields }) => {
  const classes = useStyles();
  const store =useStore();
  const data=store.getData();
  const patientData=store.getSelectedDateTime();
  const { navigate } = useRoute()
  const {setIsDateTimeSelected,setAptCreated}=useSession();
  const [loading,setLoading]=useState<boolean>(false)
  const [error,setError]=useState<any>()
  const appointmentTypeId = store?.getSelectedDateTime()?.appointmentTypeID;
  const getFormFields = store.getFormFields();
  const additionalInfo = getFormFields ? getFormFields.additionalInfo:""
  const { sortedFormFields, validationSchema } = useFormConfig( additionalInfo || formFields );
  const [updatedForm,setUpdatedForm]=useState(sortedFormFields)
  const [updatedField,setupdatedField]=useState()
  const initialValues = getInitialValues(sortedFormFields);
  const getUpdatedForm=useUpdatePatientForm(updatedForm,updatedField)

  const defaultvalues={
    firstName: getFormFields?getFormFields.firstName : "",
    lastName: getFormFields?getFormFields.lastName : "",
    email: getFormFields?getFormFields.email : "",
    phone_number: getFormFields?getFormFields.phone_number : "",
    termsAccepted: true,
    ...initialValues,
  }

  useEffect(()=>{
  setUpdatedForm(getUpdatedForm)
  store.setFormFields({...store.getFormFields(),additionalInfo:getUpdatedForm})
  },[getUpdatedForm])

  const updateRequiredFields=(event:React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>)=>{
    const fieldName = event.target.name;
    const value = event.target.value
    store.setFormFields({...store.getFormFields(),[fieldName]:value})
  }

 const submitPatientData =async (values:any)=>{
  store.setPatientData(values);
  const params: Params = {
    authenticity_token:data?.authenticity_token,
    patient_info:{first_name:values?.firstName,
      last_name:values?.lastName,
      email:values?.email,
      primary_phone:values?.phone_number,
      message_opt:"on",
      additional_info:updatedForm.map((additionalfield: any) => {
        additionalfield.value = additionalfield.value === "" ?( values?.[additionalfield.label] || "") : additionalfield.value
        return additionalfield
      })
    },
    appointment:{
      appointment_type_id:appointmentTypeId,
      appointment_time:patientData?.dateTimeOffset,
      appointment_date:patientData?.date,
      doctor_id:patientData?.doctorId,
      location_id:`${patientData?.addressID}`,
    },

  };

  try {
    setLoading(true)
    const result = await postPatientData(`${data.BaseUrl}${data.SubmissionEndpoint}`, params);
    if(result && result.appointment)
    {
      store.setAPTDetails(result)
      store.setData({...data,authenticity_token:result?.authenticity_token})
      store.clearDropDowns();
      store.clearFormFields();
      setLoading(false)
      setAptCreated(true)
      navigate(customRoutes.successAPT)
    }
  } catch (error) {
    console.error('Error:', error);
    // setLoading(false)
    setError(error)
  }
 }

 const onChange=(field:any)=>{
  setupdatedField(field)
 }

 const loadingView=()=>{
  return(
    <Grid className={classes.loaderMainBody}>
      <Grid sx={{flexDirection: "column"}} className={classes.loader} >
        <Grid style={{ height: '50px', width: '50px',fill:"#3498db"}}>
        <BallTriangle  />
        </Grid>
        <Typography sx={{ fontFamily: "Open Sans !important", marginTop: "16px" ,textAlign:"center"}}>
          Scheduling your appointment... just a moment!
        </Typography>
      </Grid>
    </Grid>
  )
 }

 const errorView=()=>{
  return<Errorview errorMessage={ErrorMessages.faildToCreateAPT} setError={setError} setLoading={setLoading}></Errorview>
 }

 const onBackPressed=()=>{
  navigate(customRoutes.DRDetails)
  setIsDateTimeSelected(false)
 }

  return (
    <>
    {!loading?(<Formik
      initialValues={defaultvalues}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        submitPatientData(values);
      }}
    >
      {({ handleSubmit}) => (
          <Form onSubmit={handleSubmit} className={classes.formkMargins} onChange={(event:any)=>{updateRequiredFields(event)}}>
          <RequiredFields />
          <Stack spacing={2}>
            {sortedFormFields.map((field: any) => {
              const FieldComponent = getFieldComponent(field.field_type_id);
              if (!FieldComponent) return null;

              return (
                <FieldComponent
                  key={field.id}
                  label={field.label}
                  name={field.label}
                  options={field?.options}
                  placeholder={field.placeholder}
                  isRequired={field.is_required}
                  errorMessage={field.error_message}
                  {...field}
                  onChange={onChange}
                  formField={field}
                />
              );
            })}
          </Stack>
          <Box my={1.5} mx={1.5}>
            <Field name="termsAccepted">
              {({ field }: FieldProps) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      {...field}
                      checked={field.value}
                      sx={{ display: "none" }}
                    />
                  }
                  label={
                    <Typography variant="body1" sx={{ fontSize: '13px',color:"grey" }}>
                      {termsAcceptedLabel}
                    </Typography>
                  }
                />
              )}
            </Field>
          </Box>
          <Grid sx={{paddingBottom:"30px"}}>
            <Grid sx={{paddingTop:"20px",paddingBottom:"10px"}}>
                <Divider></Divider>
            </Grid>
            <Grid className={classes.dialogFooterBody}>

                <Grid>
                  <Button variant='outlined'  className={classes.backButton} sx={{textTransform: "none",}} onClick={onBackPressed}>
                    {<span className={classes.text}>Go Back</span>}
                  </Button> 
                </Grid>
                <Grid>
                      <Button variant='outlined' className={classes.continueButton} sx={{textTransform: "none",}} type="submit">
                      <span className={classes.text}>Submit</span>
                      <ArrowForwardIcon className={classes.icon} /></Button>
                </Grid>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>):!error? loadingView():errorView()}
    </>
  );
};

export default FormBuilder;
